import React from 'react';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';

import {
  Container,
  Card,
  Row,
  Label,
} from 'reactstrap';
import { useHistory } from 'react-router-dom';
import { debounce } from 'lodash';
import { toast } from 'react-toastify';

import Breadcrumb from 'components/App/Breadcrumb';
import Button from 'components/App/Button';
import Meta from 'components/App/Meta';
import AppInputValidation from 'components/App/InputValidation';
import AppSelectValidation from 'components/App/SelectValidation';
import Pagination from 'components/Pagination';
import ModalRequestAction from 'components/Modals/ModalRequestAction';

import images from 'assets/images';
import { themeColor } from 'constants/index';
import cn from 'utils/classnames';
import getTotalPage from 'utils/totalPage';

import DataTableFavoritePlace from './DataTableFavoritePlace';
import ModalAddFavoritePlace from './ModalAddFavoritePlace';
import styles from './FavoritePlace.module.scss';

const breadcrumbList = [
  {
    name: 'Quản lý chỗ nghỉ',
    isActive: false,
    link: '',
  },
  {
    name: 'Danh sách yêu thích',
    isActive: true,
    link: '',
  },
];

const DEFAULT_LIMIT = 10;
const MARKET_PAGE_LIMIT = 8;

const STAR_RATING_OPTIONS = [
  { label: '5 sao', value: 5 },
  { label: '4 sao', value: 4 },
  { label: '3 sao', value: 3 },
  { label: '2 sao', value: 2 },
  { label: '1 sao', value: 1 },
];

const DEFAULT_SORT_BY = 'starRating';
const DEFAULT_SORT_ORDER = 'desc';

function FavoritePlace(props) {
  const {
    isLoadingLocationList,
    locationList,
    totalLocation,
    marketList,
    marketTotal,
    isLoadingMarketList,

    actionGetLocationList,
    actionGetMarketList,
    actionAddPlaceFavorite,
    actionDeletePlaceFavorite,
  } = props;

  const history = useHistory();
  const [marketPage, setMarketPage] = React.useState(1);
  const [marketSearchQuery, setMarketSearchQuery] = React.useState('');
  const [selectedMarketLabel, setSelectedMarketLabel] = React.useState('');
  const [sortBy, setSortBy] = React.useState(DEFAULT_SORT_BY);
  const [sortOrder, setSortOrder] = React.useState(DEFAULT_SORT_ORDER);
  const [isOpenModalDelete, setIsOpenModalDelete] = React.useState(false);
  const [placeIdDelete, setPlaceIdDelete] = React.useState('');
  const [isOpenModalShow, setIsOpenModalShow] = React.useState(false);
  const [placeIdShow, setPlaceIdShow] = React.useState('');
  const [isFavoriteShow, setIsFavoriteShow] = React.useState(false);
  const [isOpenModalAddFavorite, setIsOpenModalAddFavorite] = React.useState(false);

  const hasMoreMarkets = marketList.length < marketTotal;

  const marketOptions = React.useMemo(
    () =>
      marketList.map((item) => ({
        label: item.name,
        value: item._id,
      })),
    [marketList],
  );

  const urlPage = React.useMemo(
    () => Number(new URLSearchParams(history.location.search).get('page') || 1),
    [history.location.search],
  );

  const validation = useFormik({
    initialValues: {
      q: '',
      market: '',
      starRating: '',
    },
    onSubmit: () => {},
  });

  const filterValuesRef = React.useRef(validation.values);
  filterValuesRef.current = validation.values;

  const buildLocationListPayload = React.useCallback(
    (overrides = {}) => {
      const payload = {
        sortBy,
        sortOrder,
        page: urlPage,
        limit: DEFAULT_LIMIT,
        isFavoriteOrHot: true,
      };

      Object.entries(filterValuesRef.current).forEach(([key, value]) => {
        if (value !== '' && value != null) {
          payload[key] = value;
        }
      });

      return {
        ...payload,
        ...overrides,
      };
    },
    [sortBy, sortOrder, urlPage],
  );

  const fetchLocationList = React.useCallback(
    (overrides = {}) => {
      actionGetLocationList(buildLocationListPayload(overrides));
    },
    [actionGetLocationList, buildLocationListPayload],
  );

  const onOpenModalDelete = React.useCallback((placeId) => {
    setPlaceIdDelete(placeId);
    setIsOpenModalDelete(true);
  }, []);

  const onCloseModalDelete = React.useCallback(() => {
    setIsOpenModalDelete(false);
    setPlaceIdDelete('');
  }, []);

  const onConfirmDelete = React.useCallback(() => {
    actionDeletePlaceFavorite({
      placeId: placeIdDelete,
      callback: () => {
        toast.success('Đã xóa khỏi danh sách yêu thích');
        onCloseModalDelete();
        fetchLocationList();
      },
    });
  }, [
    placeIdDelete,
    actionDeletePlaceFavorite,
    onCloseModalDelete,
    fetchLocationList,
  ]);

  const onOpenModalShow = React.useCallback((placeId, isFavorite) => {
    setPlaceIdShow(placeId);
    setIsFavoriteShow(!!isFavorite);
    setIsOpenModalShow(true);
  }, []);

  const onCloseModalShow = React.useCallback(() => {
    setIsOpenModalShow(false);
    setPlaceIdShow('');
    setIsFavoriteShow(false);
  }, []);

  const onConfirmShow = React.useCallback(() => {
    if (isFavoriteShow) {
      actionDeletePlaceFavorite({
        placeId: placeIdShow,
        callback: () => {
          toast.success('Đã ẩn khỏi trang chủ thành công');
          onCloseModalShow();
          fetchLocationList();
        },
      });
    } else {
      actionAddPlaceFavorite({
        placeId: placeIdShow,
        callback: () => {
          toast.success('Đã hiển thị lên trang chủ thành công');
          onCloseModalShow();
          fetchLocationList();
        },
      });
    }
  }, [
    placeIdShow,
    isFavoriteShow,
    actionAddPlaceFavorite,
    actionDeletePlaceFavorite,
    onCloseModalShow,
    fetchLocationList,
  ]);

  const onOpenModalAddFavorite = React.useCallback(() => {
    setIsOpenModalAddFavorite(true);
  }, []);

  const onCloseModalAddFavorite = React.useCallback(() => {
    setIsOpenModalAddFavorite(false);
  }, []);

  const onSelectPlaceAddFavorite = React.useCallback(
    (placeId) => {
      actionAddPlaceFavorite({
        placeId,
        callback: () => {
          toast.success('Đã thêm chỗ nghỉ vào danh sách yêu thích thành công');
          onCloseModalAddFavorite();
          fetchLocationList();
        },
      });
    },
    [actionAddPlaceFavorite, fetchLocationList, onCloseModalAddFavorite],
  );

  const onSearchSubmit = React.useCallback(
    (event) => {
      event.preventDefault();
      fetchLocationList({ page: 1 });

      if (urlPage !== 1) {
        history.push('?page=1');
      }
    },
    [fetchLocationList, history, urlPage],
  );

  const onSort = React.useCallback(
    (columnSortBy) => {
      const nextSortOrder =
        sortBy === columnSortBy && sortOrder === 'desc' ? 'asc' : 'desc';
      const resolvedSortOrder =
        sortBy === columnSortBy ? nextSortOrder : DEFAULT_SORT_ORDER;

      setSortBy(columnSortBy);
      setSortOrder(resolvedSortOrder);

      if (urlPage !== 1) {
        history.push('?page=1');
        return;
      }

      fetchLocationList({
        sortBy: columnSortBy,
        sortOrder: resolvedSortOrder,
        page: 1,
      });
    },
    [fetchLocationList, history, sortBy, sortOrder, urlPage],
  );

  const onChangeFilterOption = React.useCallback(
    (e) => {
      validation.setFieldValue(e.name, e.value, true);
    },
    [validation],
  );

  const marketSelectValue = React.useMemo(() => {
    const marketId = validation.values.market;

    if (!marketId) {
      return null;
    }

    const labelFromList = marketList.find(
      (item) => item._id === marketId,
    )?.name;

    return {
      label: selectedMarketLabel || labelFromList || '',
      value: marketId,
    };
  }, [validation.values.market, selectedMarketLabel, marketList]);

  const onMarketChange = React.useCallback(
    (e) => {
      validation.setFieldValue('market', e?.value || '', true);
      setSelectedMarketLabel(e?.label || '');
    },
    [validation],
  );

  const buildMarketListPayload = React.useCallback(({ page, append, q }) => {
    const payload = {
      page,
      limit: MARKET_PAGE_LIMIT,
      append,
    };

    if (q) {
      payload.q = q;
    }

    return payload;
  }, []);

  const debouncedSearchMarkets = React.useMemo(
    () =>
      debounce((searchValue) => {
        setMarketSearchQuery(searchValue);
        setMarketPage(1);
        actionGetMarketList(
          buildMarketListPayload({
            page: 1,
            append: false,
            q: searchValue,
          }),
        );
      }, 400),
    [actionGetMarketList, buildMarketListPayload],
  );

  React.useEffect(
    () => () => debouncedSearchMarkets.cancel(),
    [debouncedSearchMarkets],
  );

  const onMarketInputChange = React.useCallback(
    (inputValue, { action }) => {
      if (action === 'input-change') {
        debouncedSearchMarkets(inputValue);
      }
    },
    [debouncedSearchMarkets],
  );

  const resetMarketList = React.useCallback(() => {
    debouncedSearchMarkets.cancel();
    setMarketSearchQuery('');
    setMarketPage(1);
    actionGetMarketList(
      buildMarketListPayload({ page: 1, append: false, q: '' }),
    );
  }, [actionGetMarketList, buildMarketListPayload, debouncedSearchMarkets]);

  const onMarketMenuOpen = React.useCallback(() => {
    resetMarketList();
  }, [resetMarketList]);

  const onMarketBlur = React.useCallback(() => {
    debouncedSearchMarkets.cancel();
  }, [debouncedSearchMarkets]);

  const onLoadMoreMarkets = React.useCallback(() => {
    if (isLoadingMarketList || !hasMoreMarkets) {
      return;
    }

    const nextPage = marketPage + 1;
    setMarketPage(nextPage);
    actionGetMarketList(
      buildMarketListPayload({
        page: nextPage,
        append: true,
        q: marketSearchQuery,
      }),
    );
  }, [
    actionGetMarketList,
    buildMarketListPayload,
    hasMoreMarkets,
    isLoadingMarketList,
    marketPage,
    marketSearchQuery,
  ]);

  const onResetForm = React.useCallback(() => {
    validation.resetForm();
    setSelectedMarketLabel('');
    debouncedSearchMarkets.cancel();
    setMarketSearchQuery('');
    setMarketPage(1);
    setSortBy(DEFAULT_SORT_BY);
    setSortOrder(DEFAULT_SORT_ORDER);
    fetchLocationList({
      sortBy: DEFAULT_SORT_BY,
      sortOrder: DEFAULT_SORT_ORDER,
      page: 1,
      q: '',
      market: '',
      starRating: '',
    });

    if (urlPage !== 1) {
      history.push('?page=1');
    }
  }, [
    debouncedSearchMarkets,
    fetchLocationList,
    history,
    urlPage,
    validation,
  ]);

  const isSearchBtnDisabled = React.useMemo(
    () =>
      validation.values.q === '' &&
      validation.values.market === '' &&
      validation.values.starRating === '',
    [validation],
  );

  const onChangePage = React.useCallback(
    (selected) => {
      history.push(`?page=${selected.selected + 1}`);
    },
    [history],
  );

  const pageCount = React.useMemo(
    () => getTotalPage(totalLocation, DEFAULT_LIMIT),
    [totalLocation],
  );

  React.useEffect(() => {
    fetchLocationList({ page: urlPage });
  }, [urlPage, fetchLocationList]);

  const startIndex = React.useMemo(
    () => (urlPage - 1) * DEFAULT_LIMIT,
    [urlPage],
  );

  return (
    <>
      <div className={`page-content ${styles.Page}`}>
        <Meta name="Danh sách yêu thích" />

        <Container fluid>
          <Breadcrumb
            pageTitle="Quản lý chỗ nghỉ"
            breadcrumbList={breadcrumbList}
          />

          <Card className="Section">
            <form onSubmit={onSearchSubmit}>
              <Row className="Section__Search__Filter">
                <div className="mb-24 flex-1">
                  <Label className="Section__Search__Label">Tìm kiếm</Label>

                  <div className="Section__Search__Container">
                    <AppInputValidation
                      name="q"
                      id="q"
                      type="text"
                      placeholder="Tìm theo ID, Tên, Email và SĐT khách"
                      validation={validation}
                      inputClassName={cn('Section__Search__Input')}
                    />

                    <i className="fas fa-search Section__Search__Icon__Search" />
                  </div>
                </div>

                <div className="flex-2">
                  <Label className="Section__Search__Label">Bộ lọc</Label>

                  <div className="Section__Filter__Container">
                    <AppSelectValidation
                      placeholder="Thị trường"
                      name="market"
                      key="market"
                      options={marketOptions}
                      validation={validation}
                      value={marketSelectValue}
                      isUsePureValue
                      onChange={onMarketChange}
                      onInputChange={onMarketInputChange}
                      onBlur={onMarketBlur}
                      onMenuOpen={onMarketMenuOpen}
                      onMenuScrollToBottom={onLoadMoreMarkets}
                      filterOption={() => true}
                      isLoading={isLoadingMarketList}
                      selectedClassName={styles.FilterInput}
                      isHiddenSearchable
                    />

                    <AppSelectValidation
                      placeholder="Hạng sao"
                      name="starRating"
                      key="starRating"
                      options={STAR_RATING_OPTIONS}
                      validation={validation}
                      value={validation.values.starRating}
                      onChange={onChangeFilterOption}
                      selectedClassName={styles.FilterInput}
                      isHiddenSearchable
                    />
                  </div>
                </div>
              </Row>

              <div className="d-flex mt-24">
                <Button
                  outline
                  classes="Section__Search__Button__ReType"
                  color={themeColor.LIGHT}
                  onClick={onResetForm}
                >
                  Nhập lại
                </Button>

                <Button
                  classes="Section__Search__Button__Search"
                  type="submit"
                  disabled={isSearchBtnDisabled}
                >
                  Tìm kiếm
                </Button>
              </div>
            </form>
          </Card>

          <Card className="Section">
            <div className="d-flex Table__Header">
              <Label className="Table__Header__Total">
                {`${totalLocation} chỗ nghỉ yêu thích`}
              </Label>

              <Button onClick={onOpenModalAddFavorite}>
                <span>Thêm chỗ nghỉ yêu thích</span>
              </Button>
            </div>

            <div className="Table__Body">
              <DataTableFavoritePlace
                isLoadingLocationList={isLoadingLocationList}
                locations={locationList}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={onSort}
                startIndex={startIndex}
                onDeleteFavorite={onOpenModalDelete}
                onShowOnHomepage={onOpenModalShow}
              />
            </div>
          </Card>

          <Pagination
            page={urlPage}
            pageCount={pageCount}
            onChangePage={onChangePage}
          />
        </Container>

        <ModalRequestAction
          isOpen={isOpenModalDelete}
          icon={images.modalGarbageCan.default}
          textRequest={
            'Bạn có chắc chắn muốn xóa \n chỗ nghỉ này khỏi danh sách yêu thích không?'
          }
          onToggle={onCloseModalDelete}
          onCloseModal={onCloseModalDelete}
          onConfirm={onConfirmDelete}
        />

        <ModalRequestAction
          isOpen={isOpenModalShow}
          icon={images.modalWarning.default}
          textRequest={
            isFavoriteShow
              ? 'Bạn có chắc chắn muốn ẩn \n chỗ nghỉ này khỏi Trang Chủ không?'
              : 'Bạn có chắc chắn muốn hiển thị \n chỗ nghỉ này lên Trang Chủ không?'
          }
          onToggle={onCloseModalShow}
          onCloseModal={onCloseModalShow}
          onConfirm={onConfirmShow}
        />

        <ModalAddFavoritePlace
          isOpen={isOpenModalAddFavorite}
          onClose={onCloseModalAddFavorite}
          onSelectPlace={onSelectPlaceAddFavorite}
        />
      </div>
    </>
  );
}

FavoritePlace.propTypes = {
  isLoadingLocationList: PropTypes.bool.isRequired,
  isLoadingMarketList: PropTypes.bool,

  locationList: PropTypes.instanceOf(Array),
  totalLocation: PropTypes.number,
  marketList: PropTypes.instanceOf(Array),
  marketTotal: PropTypes.number,

  actionGetLocationList: PropTypes.func.isRequired,
  actionGetMarketList: PropTypes.func.isRequired,
  actionAddPlaceFavorite: PropTypes.func.isRequired,
  actionDeletePlaceFavorite: PropTypes.func.isRequired,
};

FavoritePlace.defaultProps = {
  locationList: [],
  totalLocation: 0,
  marketList: [],
  marketTotal: 0,
  isLoadingMarketList: false,
};

export default FavoritePlace;
